import { Box, Popover, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import dayjs from '../../../../config/dayjs';

import RadiusButton from '../../../../components/RadiusButton';
import NOTIFICATION_BELL from '../../../../assets/images/notification-bell.svg';
import ACTION_VIEW from '../../../../assets/images/action-view.svg';
import { ALERT_TYPES, countByAlertType } from './alertTypes';

// Type filter buttons ("All 7", "USB 3", ...), shared by the bell popover and the Alert Details popup
export const AlertTypeFilters = ({ countByType, total, selectedType, onSelect }) => {
    const filters = [{ type: 'all', label: `All ${total}` }]
        .concat(Object.keys(ALERT_TYPES).filter((type) => countByType[type]).map((type) => ({ type, label: `${ALERT_TYPES[type].short} ${countByType[type]}` })));
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {filters.map((filter) => (
                <RadiusButton
                    key={filter.type}
                    size='small'
                    color='secondary'
                    variant={selectedType === filter.type ? 'contained' : 'outlined'}
                    sx={{ fontWeight: 500, textTransform: 'none', fontSize: 12 }}
                    onClick={() => onSelect(filter.type)}
                >
                    {filter.label}
                </RadiusButton>
            ))}
        </Box>
    );
}

// alertVenues: from alert-summary, one entry per venue with its open alert counts
export const AlertBell = ({ alertVenues, lastUpdated, onViewVenue, onOpenAlertTab }) => {

    const [anchorElement, setAnchorElement] = useState(null);
    const [selectedType, setSelectedType] = useState('all');
    // Popover locks page scroll while open, so "Open Alert tab" runs after it has fully closed
    const shouldOpenAlertTabRef = useRef(false);

    // Each venue's open alerts grouped by type (USB, Display, ...)
    const venues = alertVenues.map((venue) => ({ ...venue, openCountByType: countByAlertType(Object.entries(venue.openCountByCode)) }));

    // Totals over all venues: for the badge and the type filter buttons
    const totalOpenCount = venues.reduce((sum, venue) => sum + venue.openCount, 0);
    const openCountByType = countByAlertType(alertVenues.flatMap((venue) => Object.entries(venue.openCountByCode)));
    const hasOpenAlerts = totalOpenCount > 0;

    // Venues listed in the popover: those with open alerts of the selected type
    const getShownCount = (venue) => (selectedType === 'all' ? venue.openCount : venue.openCountByType[selectedType] ?? 0);
    const shownVenues = venues.filter((venue) => getShownCount(venue) > 0);

    const closePopover = () => setAnchorElement(null);

    return (
        <>
            <Box
                component='button'
                type='button'
                aria-label={hasOpenAlerts ? `Critical alerts: ${totalOpenCount} open` : 'Critical alerts: none open'}
                aria-haspopup='true'
                aria-expanded={!!anchorElement}
                onClick={(e) => setAnchorElement(e.currentTarget)}
                sx={{
                    position: 'relative',
                    width: 40,
                    height: 40,
                    display: 'grid',
                    placeItems: 'center',
                    p: 0,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: hasOpenAlerts ? 'error.main' : 'grey.300',
                    bgcolor: hasOpenAlerts ? 'rgba(230, 57, 62, 0.08)' : 'background.base',
                    borderRadius: 2,
                    '@keyframes dashboardBellRing': {
                        '0%, 80%, 100%': { transform: 'rotate(0)' },
                        '84%': { transform: 'rotate(12deg)' },
                        '88%': { transform: 'rotate(-10deg)' },
                        '92%': { transform: 'rotate(6deg)' },
                    },
                    '& img': hasOpenAlerts ? { animation: 'dashboardBellRing 3s ease-in-out infinite', transformOrigin: '50% 15%' } : {},
                    '@media (prefers-reduced-motion: reduce)': { '& img': { animation: 'none' } },
                }}
            >
                <img src={NOTIFICATION_BELL} alt='' style={{ width: 20, height: 20 }} />
                {hasOpenAlerts && (
                    <Box component='span' sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        minWidth: 20,
                        height: 20,
                        px: 0.625,
                        borderRadius: 2.5,
                        bgcolor: 'error.main',
                        color: '#FFFFFF',
                        fontSize: 11,
                        fontWeight: 700,
                        display: 'grid',
                        placeItems: 'center',
                        border: '2px solid #FFFFFF',
                    }}>
                        {totalOpenCount}
                    </Box>
                )}
            </Box>

            <Popover
                open={!!anchorElement}
                anchorEl={anchorElement}
                onClose={closePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { width: 380, maxWidth: 'calc(100vw - 32px)', mt: 1, borderRadius: 2 } } }}
                TransitionProps={{
                    onExited: () => {
                        if (shouldOpenAlertTabRef.current) {
                            shouldOpenAlertTabRef.current = false;
                            onOpenAlertTab();
                        }
                    }
                }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Critical alerts</Typography>
                        <Typography sx={{ fontSize: 12, color: 'grey.600' }}>
                            {`${shownVenues.length} venue${shownVenues.length === 1 ? '' : 's'} · ${totalOpenCount} open`}
                        </Typography>
                    </Box>

                    {hasOpenAlerts && <AlertTypeFilters countByType={openCountByType} total={totalOpenCount} selectedType={selectedType} onSelect={setSelectedType} />}

                    <Box sx={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                        {shownVenues.length
                            ? shownVenues.map((venue) => (
                                <Box key={venue.venueMasterId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'grey.300' }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>{venue.venueName ?? `Venue ${venue.venueMasterId}`}</Typography>
                                        <Typography sx={{ fontSize: 12, color: 'grey.600' }}>
                                            {Object.entries(venue.openCountByType).map(([type, count]) => `${ALERT_TYPES[type].short} ×${count}`).join(' · ')}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'error.main' }}>{getShownCount(venue)}</Typography>
                                    <Box component='span' sx={{ cursor: 'pointer', display: 'inline-flex' }} aria-label={`View alerts for ${venue.venueName ?? venue.venueMasterId}`}
                                        onClick={() => { closePopover(); onViewVenue(venue); }}>
                                        <img src={ACTION_VIEW} alt='' />
                                    </Box>
                                </Box>
                            ))
                            : <Typography sx={{ fontSize: 13, color: 'grey.500', textAlign: 'center', p: 2 }}>No open alerts. All venues are clear.</Typography>
                        }
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontSize: 12, color: 'grey.600' }}>{`Updated ${(lastUpdated ?? dayjs()).format('hh:mm A')}`}</Typography>
                        <Box component='span' sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline', fontSize: 13 }}
                            onClick={() => { shouldOpenAlertTabRef.current = true; closePopover(); }}>
                            Open Alert tab →
                        </Box>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}
